"use client";

import { Button } from "@/components/ui/button";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getUniqueLabels, getUniqueIngredients } from "@/lib/recipe-search-service";
import type { Recipe, RecipeBox } from "@/lib/types";
import { getAllLabels } from "@/lib/recipe-categories";
import type { SearchFilters } from "@/lib/recipe-search-service";
import { Filter, X } from "lucide-react";
import { useState } from "react";

interface AdvancedFiltersProps {
  readonly recipes: Recipe[];
  readonly recipeBox: RecipeBox | null;
  readonly filters: SearchFilters;
  readonly onFiltersChange: (filters: SearchFilters) => void;
  readonly onReset: () => void;
}

export function AdvancedFilters({
  recipes,
  recipeBox,
  filters,
  onFiltersChange,
  onReset,
}: AdvancedFiltersProps) {
  const [open, setOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<SearchFilters>(filters);

  const availableLabels = [
    ...new Set([
      ...getAllLabels(recipeBox?.labels ?? []),
      ...getUniqueLabels(recipes),
    ]),
  ];

  const hasActiveFilters =
    (filters.labels && filters.labels.length > 0) ||
    filters.prepTime ||
    filters.cookTime ||
    filters.isFavorite !== undefined ||
    filters.creatorUid ||
    (filters.ingredients && filters.ingredients.length > 0);

  const handleApply = () => {
    onFiltersChange(localFilters);
    setOpen(false);
  };

  const handleReset = () => {
    const emptyFilters: SearchFilters = {};
    setLocalFilters(emptyFilters);
    onFiltersChange(emptyFilters);
    onReset();
    setOpen(false);
  };

  const toggleLabel = (label: string) => {
    const currentLabels = localFilters.labels ?? [];
    const newLabels = currentLabels.includes(label)
      ? currentLabels.filter((l) => l !== label)
      : [...currentLabels, label];
    setLocalFilters({ ...localFilters, labels: newLabels });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className={hasActiveFilters ? "border-primary" : ""}>
          <Filter className="mr-2 h-4 w-4" />
          Filters
          {hasActiveFilters && (
            <span className="ml-2 rounded-full bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
              {[
                filters.labels?.length ?? 0,
                filters.prepTime ? 1 : 0,
                filters.cookTime ? 1 : 0,
                filters.isFavorite !== undefined ? 1 : 0,
                filters.creatorUid ? 1 : 0,
                filters.ingredients?.length ?? 0,
              ].reduce((a, b) => a + b, 0)}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Advanced Filters</DialogTitle>
          <DialogDescription>
            Filter recipes by multiple criteria to find exactly what you&apos;re looking for.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Labels Filter */}
          {availableLabels.length > 0 && (
            <div className="space-y-2">
              <Label>Labels</Label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border rounded-md">
                {availableLabels.map((label) => {
                  const isSelected = localFilters.labels?.includes(label) ?? false;
                  return (
                    <Button
                      key={label}
                      type="button"
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleLabel(label)}
                      className="h-8"
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Favorite Filter */}
          <div className="space-y-2">
            <Label>Favorite Status</Label>
            <select
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={
                localFilters.isFavorite === undefined
                  ? "__all__"
                  : localFilters.isFavorite
                    ? "favorite"
                    : "not-favorite"
              }
              onChange={(e) =>
                setLocalFilters({
                  ...localFilters,
                  isFavorite: e.target.value === "__all__" ? undefined : e.target.value === "favorite",
                })
              }
            >
              <option value="__all__">All recipes</option>
              <option value="favorite">Favorites only</option>
              <option value="not-favorite">Not favorites</option>
            </select>
          </div>

          {/* Prep Time Filter */}
          <div className="space-y-2">
            <Label>Prep Time</Label>
            <Input
              value={localFilters.prepTime || ""}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, prepTime: e.target.value || undefined })
              }
              placeholder="e.g., 20 minutes"
            />
          </div>

          {/* Cook Time Filter */}
          <div className="space-y-2">
            <Label>Cook Time</Label>
            <Input
              value={localFilters.cookTime || ""}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, cookTime: e.target.value || undefined })
              }
              placeholder="e.g., 45 minutes"
            />
          </div>

          {/* Ingredients Filter */}
          {(() => {
            const availableIngredients = getUniqueIngredients(recipes);
            return availableIngredients.length > 0 ? (
              <div className="space-y-2">
                <Label>Ingredients</Label>
                <Input
                  value={localFilters.ingredients?.join(", ") || ""}
                  onChange={(e) => {
                    const ingredients = e.target.value
                      .split(",")
                      .map((ing) => ing.trim())
                      .filter(Boolean);
                    setLocalFilters({
                      ...localFilters,
                      ingredients: ingredients.length > 0 ? ingredients : undefined,
                    });
                  }}
                  placeholder="e.g., chicken, tomatoes, garlic"
                />
                <p className="text-xs text-muted-foreground">
                  Separate multiple ingredients with commas
                </p>
              </div>
            ) : null;
          })()}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleReset}>
            <X className="mr-2 h-4 w-4" />
            Reset All
          </Button>
          <Button onClick={handleApply}>Apply Filters</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
